import { useEffect, useRef } from 'react';
import CollectVariableTreeNode from '../types/BotBuilder/CollectVariableTreeNode';
import SlotConditionalTreeNode from '../types/BotBuilder/SlotConditionalTreeNode';
import Slot from '../types/BotBuilder/Slot';
import BotResponseTreeNode from '../types/BotBuilder/BotResponseTreeNode';
import SlotValueType from '../types/BotBuilder/SlotValueType';

interface UseSlotConditionalsSyncProps {
  collectVariableNode: CollectVariableTreeNode | null;
  selectedSlot: Slot | null;
  onNodeUpdate: (node: BotResponseTreeNode) => void;
  dataNode: BotResponseTreeNode;
}

function getEnumeratedValuesSignature(slot: Slot | null): string {
  if (!slot) return '';

  if (slot.valueType !== SlotValueType.ENUMERATED_VALUE) return '';
  if (!slot.enumeratedSlotValues || slot.enumeratedSlotValues.length === 0) {
    return '';
  }

  return slot.enumeratedSlotValues
    .map((v) => v.resolvedValue)
    .sort((a, b) => a.localeCompare(b))
    .join('|');
}

function useSlotConditionalsSync({
  collectVariableNode,
  selectedSlot,
  onNodeUpdate,
  dataNode
}: UseSlotConditionalsSyncProps): void {
  const lastSelectedSlotIdRef = useRef<number | null>(null);
  const lastEnumeratedSignatureRef = useRef<string>('');

  useEffect(() => {
    if (!collectVariableNode || !selectedSlot) return;

    const currentSignature = getEnumeratedValuesSignature(selectedSlot);
    const slotIdUnchanged = selectedSlot.id === lastSelectedSlotIdRef.current;
    const enumeratedUnchanged =
      currentSignature === lastEnumeratedSignatureRef.current;

    if (slotIdUnchanged && enumeratedUnchanged) return;

    const updates: Partial<CollectVariableTreeNode> = {};

    if (selectedSlot.valueType === SlotValueType.ENUMERATED_VALUE) {
      const slotValues = selectedSlot.enumeratedSlotValues.map(
        (enumVal: { resolvedValue: string }) => enumVal.resolvedValue
      );

      updates.slotConditionals = slotValues.map((slotValue) => {
        const existing = collectVariableNode.slotConditionals?.find(
          (conditional) => conditional.slotValue === slotValue
        );
        return new SlotConditionalTreeNode(
          slotValue,
          existing ? existing.nextStep : null
        );
      });
    } else {
      updates.slotConditionals = [];
    }

    Object.assign(collectVariableNode, updates);

    onNodeUpdate(dataNode);
    lastSelectedSlotIdRef.current = selectedSlot.id;
    lastEnumeratedSignatureRef.current = currentSignature;
  }, [collectVariableNode, selectedSlot, onNodeUpdate, dataNode]);
}

export default useSlotConditionalsSync;
